import React, { Children } from 'react';
import { isFragment } from 'react-is';
import PropTypes from 'prop-types';
import sum from 'lodash/sum';
import flatten from 'lodash/flatten';

import cn from '../../lib/class-name';

const mapChildrenWithFragments = (children, iterator) =>
  flatten(
    Children.map(children, (child) => {
      if (isFragment(child)) {
        return Children.toArray(child.props.children);
      }
      return child;
    })
  )
    .filter((child) => child)
    .map(iterator);

/**
 * The `Table` component. Use an array of `TableHeaderCell`s for the header and `TableRow`s as children.
 * Column widths have to be defined on each `TableHeaderCell` and affect the whole column.
 */
const Table = ({ classNames, header, children, hideHeader = false }) => {
  const widths = mapChildrenWithFragments(
    header,
    ({ props: { width = 1, fixed } }) => (fixed ? 0 : width)
  );

  const totalWidths = sum(widths);
  const computeWidth = (width, fixed) =>
    fixed ? `${width}px` : `${(width / totalWidths) * 100}%`;
  const computedWidths = Children.map(
    header,
    ({ props: { width = 1, fixed } }) => computeWidth(width, fixed)
  );

  return (
    <div className={cn('hpl2-Table', { hideHeader }, classNames)}>
      <div className="hpl2-Table__header">
        {mapChildrenWithFragments(header, (headCell, i) =>
          React.cloneElement(headCell, {
            computedWidth: computedWidths[i],
            key: i,
          })
        )}
      </div>
      <div className="hpl2-Table__content">
        {mapChildrenWithFragments(children, (child, i) =>
          React.cloneElement(child, { computedWidths, key: i })
        )}
      </div>
    </div>
  );
};

Table.displayName = 'Table';
Table.propTypes = {
  /** Optional array of CSS utility classes. */
  classNames: PropTypes.arrayOf(PropTypes.string),
  /** Array of `TableHeaderCell`s for the header. */
  header: PropTypes.node.isRequired,
  /** To hide the table header set this to true. */
  hideHeader: PropTypes.bool,
  /** `TableRow`s, `TableRowGroup`s or `TableSection`s as children. */
  children: PropTypes.node.isRequired,
};

export default Table;
